import { makeStyles, makeResetStyles, mergeClasses } from '@fluentui/react-components';
import { tokens } from '@fluentui-copilot/tokens';
import { tokens as faiTokens } from '@fluentui-copilot/tokens';
import type { ScrollDownButtonSlots, ScrollDownButtonState } from './ScrollDownButton.types';
import type { SlotClassNames } from '@fluentui/react-components';

export const scrollDownButtonClassNames: SlotClassNames<ScrollDownButtonSlots> = {
  root: 'fai-ScrollDownButton',
  spinner: 'fai-ScrollDownButton__spinner',
  spinnerMaskStroke: 'fai-ScrollDownButton__spinnerMaskStroke',
  spinnerMaskBackground: 'fai-ScrollDownButton__spinnerMaskBackground',
  spinnerBase: 'fai-ScrollDownButton__spinnerBase',
  icon: 'fai-ScrollDownButton__icon',
};

const sizes = {
  width: '36px',
  radius: '18px',
  strokeWidth: tokens.strokeWidthThickest,
};

/**
 * Styles for the root slot
 */
const useRootClassName = makeResetStyles({
  height: sizes.width,
  width: sizes.width,
  backgroundColor: tokens.colorTransparentBackground,
  borderRadius: tokens.borderRadiusCircular,
  display: 'flex',
  alignItems: 'center',
  padding: '0',
  border: 'none',
  position: 'relative',
});

const useIconStyles = makeStyles({
  isGenerating: {
    width: `calc(${sizes.width} - 2 * ${sizes.strokeWidth})`,
    height: `calc(${sizes.width} - 2 * ${sizes.strokeWidth})`,
    margin: sizes.strokeWidth,
  },
});

const useIconClassName = makeResetStyles({
  display: 'flex',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  position: 'absolute',
  borderRadius: tokens.borderRadiusCircular,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: tokens.colorNeutralBackground1,

  color: tokens.colorBrandForeground1,
  fontSize: '20px',

  ':hover': {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },

  ':hover:active': {
    backgroundColor: tokens.colorNeutralBackground1Pressed,
  },
});

const useSpinnerClassName = makeResetStyles({
  position: 'absolute',
  width: 'inherit',
  height: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animationDuration: '1.5s',
  animationTimingFunction: 'linear',
  animationIterationCount: 'infinite',
  animationName: { '100%': { transform: 'rotate(360deg)' } },
});

// inner circle styles
const useInnerCircleClassName = makeResetStyles({
  fill: tokens.colorNeutralBackground1,
  transform: `translate(${sizes.radius}, ${sizes.radius})`,
});

// outer circle styles
const useOuterCircleClassName = makeResetStyles({
  fill: tokens.colorNeutralBackground1,
  opacity: '0.6',
  transform: `translate(${sizes.radius}, ${sizes.radius})`,
});

const useStyles = makeStyles({
  rootSending: {
    background: `conic-gradient(from 0deg at 50% 50%, ${faiTokens.colorBrandFlair1} 0deg, ${faiTokens.colorBrandFlair2} 90deg, ${faiTokens.colorBrandFlair3} 180deg, ${faiTokens.colorBrandFlair2} 270deg, ${faiTokens.colorBrandFlair1} 360deg)`,
  },
});

/**
 * Apply styling to the ScrollDownButton slots based on the state
 */
export const useScrollDownButtonStyles_unstable = (state: ScrollDownButtonState): ScrollDownButtonState => {
  'use no memo';

  const styles = useStyles();
  const rootClassName = useRootClassName();
  const iconClassName = useIconClassName();
  const iconStyles = useIconStyles();
  const innerCircleClassName = useInnerCircleClassName();
  const outerCircleClassName = useOuterCircleClassName();
  const spinnerClassName = useSpinnerClassName();

  state.root.className = mergeClasses(
    scrollDownButtonClassNames.root,
    rootClassName,
    state.isGenerating && styles.rootSending,
    state.root.className,
  );
  if (state.icon) {
    state.icon.className = mergeClasses(
      scrollDownButtonClassNames.icon,
      iconClassName,
      state.isGenerating && iconStyles.isGenerating,
      state.icon.className,
    );
  }

  if (state.spinner) {
    state.spinner.className = mergeClasses(
      scrollDownButtonClassNames.spinner,
      spinnerClassName,
      state.spinner.className,
    );
  }

  if (state.spinnerMaskStroke) {
    state.spinnerMaskStroke.className = mergeClasses(
      scrollDownButtonClassNames.spinnerMaskStroke,
      innerCircleClassName,
      state.spinnerMaskStroke.className,
    );
  }

  if (state.spinnerMaskBackground) {
    state.spinnerMaskBackground.className = mergeClasses(
      scrollDownButtonClassNames.spinnerMaskBackground,
      innerCircleClassName,
      state.spinnerMaskBackground.className,
    );
  }

  if (state.spinnerBase) {
    state.spinnerBase.className = mergeClasses(
      scrollDownButtonClassNames.spinnerBase,
      outerCircleClassName,
      state.spinnerBase.className,
    );
  }

  return state;
};
